import { Link, Pencil, SlidersHorizontal, Trash2 } from 'lucide-react';
import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { DeviceDto } from 'modules/core/supabase/tables/device';
import { TokenStorage } from 'modules/core/token-storage';
import { DevicesNavigationState } from 'modules/devices/domain/constants';
import DeviceForm, { DeviceFormValues } from 'modules/devices/ui/components/device-form/device-form';
import { useCreateDevice } from 'modules/devices/ui/hooks/use-create-device';
import { useDeleteDevice } from 'modules/devices/ui/hooks/use-delete-device';
import { useDevicesList } from 'modules/devices/ui/hooks/use-devices-list';
import { useUpdateDevice } from 'modules/devices/ui/hooks/use-update-device';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getNormalizedDate } from 'shared/utils/get-normalized-date';
import { theme } from 'styles/theme/theme';
import { UDButton, UDText } from 'ud-ui/components';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';
import EmptyButton from 'ud-ui/components/empty-button';
import ImgNotFound from 'ud-ui/components/images/not-found.png';
import Modal from 'ud-ui/components/modal';
import UDPagination from 'ud-ui/components/pagination';
import UDPerPage from 'ud-ui/components/per-page/per-page';
import { UDTable } from 'ud-ui/components/table';
import UDTippy from 'ud-ui/components/tippy';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import * as S from './styles';

function DevicesPage() {
  const { setHeaderContent } = useMainLayout();
  const { userTournamentId: tournamentId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { devices, isLoading, isError, page, perPage, pages, setPage, setPerPage, refetch } = useDevicesList(tournamentId);

  const [selectedDevice, setSelectedDevice] = useState<DeviceDto | undefined>(undefined);

  const [isOpenForm, setIsOpenForm] = useState(false);
  const openForm = useCallback(() => setIsOpenForm(true), []);
  const closeForm = useCallback(() => setIsOpenForm(false), []);

  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const openDelete = useCallback(() => setIsOpenDelete(true), []);
  const closeDelete = useCallback(() => setIsOpenDelete(false), []);

  // Пришли с кнопки «Ссылка на табло для OBS» в карточке матча — объясняем,
  // куда переехал функционал, и сбрасываем флаг, чтобы окно не всплыло при возврате.
  const [isOpenOverlayNotice, setIsOpenOverlayNotice] = useState(() =>
    Boolean((location.state as DevicesNavigationState)?.fromMatchOverlay),
  );
  const closeOverlayNotice = useCallback(() => {
    setIsOpenOverlayNotice(false);
    navigate(Pages.DEVICES.INDEX, { replace: true, state: null });
  }, [navigate]);

  const openCreateModal = useCallback(() => {
    setSelectedDevice(undefined);
    openForm();
  }, [openForm]);

  const openEditModal = useCallback(
    (device: DeviceDto) => {
      setSelectedDevice({ ...device });
      openForm();
    },
    [openForm],
  );

  const openDeleteModal = useCallback(
    (device: DeviceDto) => {
      setSelectedDevice(device);
      openDelete();
    },
    [openDelete],
  );

  const createDevice = useCreateDevice(tournamentId, refetch);
  const updateDevice = useUpdateDevice(refetch);
  const deleteDevice = useDeleteDevice(refetch);

  const onSubmit = useCallback(
    async (values: DeviceFormValues) => {
      if (selectedDevice) {
        await updateDevice.mutate(selectedDevice.id, { name: values.name });
      } else {
        await createDevice.mutate({ name: values.name });
      }
      closeForm();
    },
    [selectedDevice, createDevice, updateDevice, closeForm],
  );

  const onDelete = useCallback(async () => {
    if (!selectedDevice) return;
    await deleteDevice.mutate(selectedDevice.id);
    closeDelete();
  }, [selectedDevice, deleteDevice, closeDelete]);

  const tokenStorage = useMemo(() => new TokenStorage(), []);

  const copyLink = useCallback((path: string) => {
    navigator.clipboard.writeText(window.location.origin + path).then(() => {
      toast.success('Ссылка скопирована');
    });
  }, []);

  const handleCopyLink = useCallback(
    (device: DeviceDto) => {
      const token = tokenStorage.getToken() ?? '';
      copyLink(Pages.OVERLAY_V2.builders.index(String(tournamentId), String(device.id), token));
    },
    [copyLink, tournamentId, tokenStorage],
  );

  // Панель управления живёт по тому же устройству, что и оверлей: со счётом, таймом и
  // составами работают из неё, пока оверлей показывается в OBS.
  const handleCopyControlPanelLink = useCallback(
    (device: DeviceDto) => {
      const token = tokenStorage.getToken() ?? '';
      copyLink(Pages.OVERLAY_V2.builders.controlPanel(String(tournamentId), String(device.id), token));
    },
    [copyLink, tournamentId, tokenStorage],
  );

  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory title="Устройства" createButtonOnClick={openCreateModal} createButtonText="Создать устройство" />,
    );
    return () => setHeaderContent(null);
  }, [openCreateModal, setHeaderContent]);

  return (
    <S.MainBlock>
      <div className="mb-4 flex justify-end">
        <UDPerPage value={perPage} onChange={setPerPage} />
      </div>

      <div>
        <UDTable isLoading={isLoading}>
          <thead>
            <tr>
              <th className="w-full">Название</th>
              <th className="w-[180px] min-w-[180px]">Дата создания</th>
              <th className="w-[36px] min-w-[36px]" />
              <th className="w-[36px] min-w-[36px]" />
              <th className="w-[36px] min-w-[36px]" />
              <th className="w-[36px] min-w-[36px]" />
            </tr>
          </thead>
          <tbody>
            {!isLoading && isError && (
              <tr>
                <td colSpan={6}>
                  <UDText variant="body-3" className="py-4 text-center">
                    Не удалось загрузить устройства
                  </UDText>
                </td>
              </tr>
            )}
            {!isLoading && !isError && devices.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <div className="flex h-[300px] w-full flex-col items-center justify-center gap-2">
                    <img src={ImgNotFound} alt="" className="max-h-[200px] opacity-80" />
                    <span className="text-WhiteGray text-sm">Нет устройств</span>
                    <UDButton variant="primary" onClick={openCreateModal} className="mt-2">
                      Создать устройство
                    </UDButton>
                  </div>
                </td>
              </tr>
            )}
            {!isLoading &&
              !isError &&
              devices.map((device) => (
                <tr key={device.id}>
                  <td>{device.name}</td>
                  <td>{getNormalizedDate(device.created_at)}</td>
                  <td>
                    <UDTippy content="Редактировать" placement="top">
                      <EmptyButton icon={<Pencil size={20} />} className="w-full" onClick={() => openEditModal(device)} />
                    </UDTippy>
                  </td>
                  <td>
                    <UDTippy content="Копировать ссылку на табло" placement="top">
                      <EmptyButton icon={<Link size={20} />} className="w-full" onClick={() => handleCopyLink(device)} />
                    </UDTippy>
                  </td>
                  <td>
                    <UDTippy content="Копировать ссылку на панель управления" placement="top">
                      <EmptyButton
                        icon={<SlidersHorizontal size={20} />}
                        className="w-full"
                        onClick={() => handleCopyControlPanelLink(device)}
                      />
                    </UDTippy>
                  </td>
                  <td>
                    <UDTippy content="Удалить" placement="top">
                      <EmptyButton
                        color={theme.colors.error}
                        icon={<Trash2 size={20} />}
                        className="w-full"
                        onClick={() => openDeleteModal(device)}
                      />
                    </UDTippy>
                  </td>
                </tr>
              ))}
          </tbody>
        </UDTable>
      </div>

      <div className="my-4 flex">
        <UDPagination pages={pages} currentPage={page} onPageChange={setPage} disabled={isLoading} />
      </div>

      <Modal show={isOpenForm} onClose={closeForm}>
        <UDText as="h2" variant="title-3" className="mb-7.5">
          {selectedDevice ? 'Редактирование устройства' : 'Создание устройства'}
        </UDText>
        <DeviceForm initValue={selectedDevice} onSubmit={onSubmit} />
      </Modal>

      <Modal show={isOpenOverlayNotice} onClose={closeOverlayNotice}>
        <Modal.Header title="Ссылка на табло перенесена" />

        <Modal.Body className={'w-120'}>
          <UDText variant="body-3" className="mb-2">
            Ссылка на табло больше не привязана к матчу — теперь она принадлежит устройству.
          </UDText>
          <UDText variant="body-3" className="mb-2">
            Скопируйте ссылку нужного устройства в этой таблице и вставьте её в OBS один раз: дальше она сама подхватит матч, который
            вы запустите.
          </UDText>
          <UDText variant="body-3" className={'mb-2'}>
            Запуск конкретного матча и выбор устройства выполняется в мобильном приложении ImGame.
          </UDText>
          <UDText variant="body-3">Старый формат ссылок больше не поддерживается.</UDText>
        </Modal.Body>

        <Modal.Footer justify={'center'}>
          <UDButton variant="primary" onClick={closeOverlayNotice} stretch>
            Понятно
          </UDButton>
        </Modal.Footer>
      </Modal>

      <Modal show={isOpenDelete} onClose={closeDelete}>
        <Modal.Header title="Удалить устройство?" />

        <Modal.Body>
          <UDText variant="body-3">Вы уверены что хотите удалить устройство:</UDText>
          <UDText variant="body-1">{selectedDevice?.name}</UDText>
        </Modal.Body>

        <Modal.Footer justify="end">
          <UDButton variant="secondary" onClick={closeDelete} disabled={deleteDevice.isLoading} stretch>
            Отмена
          </UDButton>
          <UDButton variant="primary" onClick={onDelete} loading={deleteDevice.isLoading} stretch>
            Удалить
          </UDButton>
        </Modal.Footer>
      </Modal>
    </S.MainBlock>
  );
}

export default DevicesPage;
